import { generateSetChild, generateAddArrayChild, generateUpdateArrayChild, generateRemoveArrayChild, generateSetMapChild } from '@/helpers/VuexHelpers'
import { service } from '@/service'
import cookie from 'cookie_js'
import { config } from '@/config'
import { dayjs, dayjsServer } from '@/helpers/dayjs'
import orderTemplate from '../order/orderTemplate'
import { gtmUpdateCart } from '@/helpers/TrackingHelpers'

let cartPromise = null

let template = orderTemplate({ config, orderEditable: true, storeType: 'cart', useCookie: true })

export default {
	namespaced: true,
	state: {
		...template.state,
		isDetached: false,
		items: [],
		initialLoad: false,
		productMap: {},
		basketToken: null,
		basketChannel: "",
		serverData: null,
		loading: false,
		openRequests: 0,
		vouchers: [],
		tip: null,
		co2Compensation: false,
		surcharges: [],
		deliveryType: 0,
		changedItems: null,
		updatedAt: 0,
		showContinue: true,
	},
	actions: {
		...template.actions,
		async open({commit, dispatch, state}, {channel, zip, shippingCode, orderNow = null, deliverTime, alternativeBrand = false, oldToken = null, referralToken = null, reservationHash = null, reservationUser = null, outdoorDeliveryId = null}) {
			//set channel
			commit('setBasketChannel', { item: channel })
			try {
				let cart = await service
					.carts({ channel: state.basketChannel, zip, shippingCode, orderNow, deliverTime, oldToken, referralToken, brand: config.BRAND, alternativeBrand, reservationHash, reservationUser, outdoorDeliveryId })

				if (cart) {
					await dispatch('readCart', { cart, ignoreDetached: true })

					// eslint-disable-next-line require-atomic-updates
					state.initialLoad = true
				}
				commit('setLoading', { item: false })
			} catch (err) {
				commit('setLoading', { item: false })
				throw err
			}
			return state.basketToken
		},

		async restoreCart({commit, state}, { token } = {}) {
			if (token && token + '' === token) {
				commit('setBasketToken', { item: token })
			}
			else {
				//read cookie
				let basketCookie = cookie.get(config.CART_COOKIE)
				if (state.basketToken == null && basketCookie) {
					commit('setBasketToken', { item: basketCookie })
				}
			}
		},

		async addItems({ commit, state, dispatch, getters }, { items, accumulatorGroup = "cart" }) {
			commit('setLoading', { item: true })
			commit('setOpenRequests', { item: state.openRequests + 1 })
			try {
				let cart = await service.addCartItems({
					token: state.basketToken,
					items,
					channel: state.basketChannel,
					accumulatorGroup
				})
				if (cart) {
					commit('setOpenRequests', { item: state.openRequests - 1 })
					await dispatch('readCart', { cart })
					commit('setLoading', { item: false })

					const stateItems = [...getters.items].reverse();
					const newItems = items.map(item => ({
						...stateItems.find(i => i.code === item.productCode && i.variantCode === item.variantCode),
						quantity: item.quantity,
						isCrossSell: !!item.isCrossSell,
					}));
					gtmUpdateCart({
						action: 'add',
						items: newItems,
						channel: state.basketChannel,
						currency: state.serverData.payments?.[0]?.price?.currency,
					})
				}
			} catch(err) {
				commit('setOpenRequests', { item: state.openRequests - 1 })
				commit('setLoading', { item: false })
				throw err;
			}

			// try to return items added
			// HACK: we return only the last item
			return state.items.filter(v => v.isNew).pop()
		},

		async updateItem({commit, state, dispatch, getters}, { identifier, quantity, accumulatorGroup = "cart" }) {
			commit('setLoading', { item: true })
			commit('setOpenRequests', { item: state.openRequests + 1 })

			// // we remove the item directly bevor the service has finished
			// if (quantity <= 0) {
			// 	commit("removeItem", { item: { id: identifier } })
			// } else {
			// 	let item = state.items.find(v => v.id == identifier)
			// 	item.quantity = quantity
			// 	commit("updateItem", { item: item })
			// }

			const item = {
				...getters.items.find(item => item.id === identifier)
			};

			try {
				let cart = await (
					quantity <= 0
					? service.removeCartItem({
							token: state.basketToken,
							identifier,
							channel: state.basketChannel,
							accumulatorGroup
						})
					: service.updateCartItem({
							token: state.basketToken,
							identifier, quantity,
							channel: state.basketChannel,
							accumulatorGroup
						})
				)
				if (cart) {
					commit('setOpenRequests', { item: state.openRequests - 1 })
					await dispatch('readCart', { cart })
					commit('setLoading', { item: false })
					gtmUpdateCart({
						action: quantity < item.quantity ? 'remove' : 'add',
						items: [{
							...item,
							quantity: Math.abs(item.quantity - quantity),
						}],
						channel: state.basketChannel,
						currency: state.serverData.payments?.[0]?.price?.currency,
					})
				}
			} catch(err) {
				commit('setOpenRequests', { item: state.openRequests - 1 })
				commit('setLoading', { item: false })
				config.DEBUG && console.error(err)
				throw err;
			}
		},

		async updateItemNotes({commit, state, dispatch}, { identifier, notes, accumulatorGroup = "cart" }) {
			commit('setLoading', { item: true })
			commit('setOpenRequests', { item: state.openRequests + 1 })

			// // we remove the item directly bevor the service has finished
			// let item = state.items.find(v => v.id == identifier)
			// item.notes = notes
			// commit("updateItem", { item: item })

			try {
				let cart = await service.updateCartItemNotes({
					token: state.basketToken,
					identifier,
					notes,
					channel: state.basketChannel,
					accumulatorGroup
				})
				if (cart) {
					commit('setOpenRequests', { item: state.openRequests - 1 })
					await dispatch('readCart', { cart })
					commit('setLoading', { item: false })
				}
			} catch(err) {
				commit('setOpenRequests', { item: state.openRequests - 1 })
				commit('setLoading', { item: false })
				config.DEBUG && console.error(err)
				throw err;
			}
		},

		async removeItem({commit, state, dispatch, getters}, { identifier, accumulatorGroup = "cart" }) {
			commit('setLoading', { item: true })
			commit('setOpenRequests', { item: state.openRequests + 1 })

			const item = {
				...getters.items.find(item => item.id === identifier)
			};

			try {
				let cart = await service.removeCartItem({ token: state.basketToken, identifier, channel: state.basketChannel, accumulatorGroup})
				if (cart) {
					commit('setOpenRequests', { item: state.openRequests - 1 })
					await dispatch('readCart', { cart })
					commit('setLoading', { item: false })
					gtmUpdateCart({
						action: 'remove',
						items: [item],
						channel: state.basketChannel,
						currency: state.serverData.payments?.[0]?.price?.currency,
					})
				}
			} catch(err) {
				commit('setOpenRequests', { item: state.openRequests - 1 })
				commit('setLoading', { item: false })
				config.DEBUG && console.error(err)
				throw err;
			}
		},

		async copyItem({ commit, state, dispatch, getters}, { identifier, accumulatorGroup = 'cart' }) {
			commit('setLoading', { item: true })
			commit('setOpenRequests', { item: state.openRequests + 1 })


			try {
				const item = {
					...getters.items.find(item => item.id === identifier)
				};
				const items = [
					{
							"quantity": 1,
							"productCode": item.code,
							"variantCode": item.variantCode,
							"childCodes": item.children.map(v => v.code),
							"forceCopy": true,
					}
				]
				let cart = await service.addCartItems({ token: state.basketToken, items, channel: state.basketChannel, accumulatorGroup})
				commit('setOpenRequests', { item: state.openRequests - 1 })
				if (cart) {
					await dispatch('readCart', { cart })
					gtmUpdateCart({
						action: 'add',
						items: [item],
						channel: state.basketChannel,
						currency: state.serverData.payments?.[0]?.price?.currency,
					})
				}
				commit('setLoading', { item: false })
			} catch (err) {
				commit('setOpenRequests', { item: state.openRequests - 1 })
				commit('setLoading', { item: false })
				config.DEBUG && console.error(err)
				throw err;
			}
		},

		async addCoupon({commit, state, dispatch}, {coupon, email, useDishes}) {
			commit('setLoading', { item: true })
			try {
				let cart = await service.addCoupon({token: state.basketToken, coupon, email, useDishes, channel: state.basketChannel})
				if (cart) {
					await dispatch('readCart', { cart })
				}
				commit('setLoading', { item: false })
				return {};
			} catch(err) {
				config.DEBUG && console.error(err)
				commit('setLoading', { item: false })
				throw err;
			}
		},

		async removeCoupon({commit, state, dispatch}) {
			commit('setLoading', { item: true })
			let cart = await service.removeCoupon({token: state.basketToken, channel: state.basketChannel})
			if (cart) {
				await dispatch('readCart', { cart })
				commit('setLoading', { item: false })
			}
		},

		async replaceCartItemChildren({ commit, state, dispatch, getters }, { identifier, childCodes, accumulatorGroup = "cart" }) {
			commit('setLoading', { item: true })
			commit('setOpenRequests', { item: state.openRequests + 1 })

			const item = {
				...getters.items.find(item => item.id === identifier)
			};
			let cart = await service.replaceCartItemChildren({ token: state.basketToken, identifier, childCodes, channel: state.basketChannel, accumulatorGroup})
			if (cart) {
				commit('setOpenRequests', { item: state.openRequests - 1 })
				await dispatch('readCart', { cart })
				commit('setLoading', { item: false })

				const newItem = {
					...getters.items.find(item => item.id === identifier)
				};
				gtmUpdateCart({
					action: 'add',
					items: newItem.children.reduce((children, child) => {
						if (!item.children.find(c => c.code === child.code)) {
							children.push(child);
						}
						return children;
					}, []),
					channel: state.basketChannel,
					currency: state.serverData.payments?.[0]?.price?.currency,
				});
				gtmUpdateCart({
					action: 'remove',
					items: item.children.reduce((children, child) => {
						if (!newItem.children.find(c => c.code === child.code)) {
							children.push(child);
						}
						return children;
					}, []),
					channel: state.basketChannel,
					currency: state.serverData.payments?.[0]?.price?.currency,
				});
			}
		},

		async exists({commit, state, dispatch}, {token, detached = false, accumulatorGroup = null} = {}) {
			if (token && token + '' === token) {
				commit('setIsDetached', { item: !!detached });
			}
			else {
				token = null;
			}
			if (token == null) {
				token = state.basketToken
			}
			if (token == null) {
				//read cookie
				const basketCookie = cookie.get(config.CART_COOKIE)
				if (basketCookie) {
					token = basketCookie
					commit('setBasketToken', { item: basketCookie })
				}
			}
			if (!token) {
				return Promise.reject(Error("Cart::exists no basketToken"))
			}

			let result = await dispatch('load', {token, accumulatorGroup})
			if (!result) {
				return Promise.reject(Error("Cart::exists no valid basket"))
			}

		},
		async restoreOrder({ commit, dispatch, state }, { token }) {
			const params = { token, channel: state.basketChannel, keepOrder: true };

			if (state.basketToken != null && state.basketToken != token) {
				params.oldToken = state.basketToken;
			}

			let result = await service.orderRestore(params)
			commit('setBasketToken', { item: result.tokenValue })
      if (result.groupOrderParticipantToken) {
        await dispatch('grouporder/restoreGroupOrder', { token: result.groupOrderParticipantToken }, { root: true })
      }
			if (result.notAvailableItems) {
				const changedItems = result.notAvailableItems
					.map(v => ({
						id: v.id,
						name: v.product.name,
						image: v.product.images.length && v.product.images[0].path || "",
					}))
				commit('setChangedItems', { item: changedItems })
			}
			return result
		},
		async updateDeliverTime({ commit, dispatch, state }, { orderNow, deliverTime, confirmChange, accumulatorGroup = null} = {}) {
			commit('setLoading', { item: true })
			try {
				deliverTime = orderNow ? null : deliverTime; // the server might return an error if deliverTime is set when orderNow is true
				let result = await service.updateDeliverTime({ orderNow, deliverTime, confirmChange, token: state.basketToken, channel: state.basketChannel, accumulatorGroup })
				if (result.cart) {
					commit('setChangedItems', { item: null })
					await dispatch('readCart', { cart: result.cart })
				}
				commit('setLoading', { item: false })
			} catch(err) {
				commit('setLoading', { item: false })
				if (err.response.data.notAvailableItems) {
					const changedItems = err.response.data.notAvailableItems
						.map(v => ({
							id: v.id,
							name: v.product.name,
							image: v.product.images.length && v.product.images[0].path || "",
						}))
					commit('setChangedItems', { item: changedItems })
				}
				throw err
			}
		},

		setCo2Compensation({ commit }, { value }) {
			commit('setCo2Compensation', { item: value })
		},
		clearCartChanges({ commit }) {
			commit('setChangedItems', { item: null })
		},
		showContinue({ commit }, { value }) {
			commit('setShowContinue', { item: value });
		},
		async lockGroupOrder({ state, commit, dispatch }, { locked = true, accumulatorGroup = null} = {}) {
			commit('setLoading', { item: true })
			commit('setOpenRequests', { item: state.openRequests + 1 })
			let cart = await service.lockGroupOrderCart({ token: state.basketToken, locked, accumulatorGroup })
			commit('setOpenRequests', { item: state.openRequests - 1 })
			if (cart.tokenValue) {
				await dispatch('readCart', { cart })
			}
			commit('setLoading', { item: false })
		},
    async createDonationOrder({ dispatch }, { amount }) {
      const result = await service.createDonation({ amount })

      dispatch('redirect', {
				name: 'checkout-token',
				params: {
					basketToken: result.tokenValue
				}
			}, {root: true})
    },
    async createPaymentFallbackOrder({ dispatch }, { amount, channel }) {
      const result =  await service.createPaymentFallback({ amount, channel })

      dispatch('redirect', {
				name: 'checkout-token',
				params: {
					basketToken: result.tokenValue
				}
			}, {root: true})
    },
	},
	getters: {
		...template.getters,
		hasBasket(state) {
			return !!state.serverData
		},
		basketState(state) {
			return state.serverData
				? state.serverData.checkoutState
				: "none"
		},
		discountCode(state) {
			return state.serverData
				? state.serverData.couponCode
				: null
		},
		deliveryPrice(state) {
			return state.serverData
				? state.serverData.totals.shipping
				: 0
		},
		deliveryTime(state) {
			//in minutes
			return state.serverData
				? state.serverData.zip
					? state.serverData.zip.deliveryTime
					: 0
				: 0
		},
		minCartPrice(state) {
			return (state.serverData?.zip?.minDeliveryAmount || 0) * 100
		},
		minCartPriceTime(state) {
			return state.serverData?.zip?.minDeliveryStart && state.serverData?.zip?.minDeliveryEnd
				? { start: state.serverData?.zip?.minDeliveryStart, end: state.serverData?.zip?.minDeliveryEnd }
				: null
		},
		isCartValidForCheckout(state, getters) {
			return getters.totalItemsWithoutAdditional > 0
				&& getters.minCartPrice <= (state.serverData.totals.items + Math.abs(state.serverData.totals.promotion)) // HACK: this doesn't seem like a very good solution ;)
		},
		shippingMethod(state) {
			return state.serverData
				? state.serverData.shipments[0].method
				: null
		},
		shopLocation(state) {
			return state.serverData
				? state.serverData.channel
				: null
		},
		channelName(state) {
			return state.serverData
				? state.serverData.channel.name
				: null
		},
		cartIncludesAlcohol(state) {
			return state.serverData
				? state.serverData.alcoholCheck == true
				: false
		},
		cartDeliveryTime(state) {
			return state.serverData
				? dayjsServer(state.serverData.deliverTime)
				: null
		},
		cartTip (state, getters) {
			if (state.tip) {
				return state.tip.total
			}
			return getters.cartTipDefault
		},
		cartTipEnabled (state) {
			return state.serverData
				? (state.serverData.channel.location.tipEnabled || false)
				: null
		},
		cartTipDefault (state) {
			return state.serverData
				? (state.serverData.channel.location.defaultTip || 0)
				: null
		},
		cartChannelHasCo2Compensation (state) {
			return state.serverData
				? state.serverData.channel.location.hasCo2Compensation
				: false
		},
		cartCo2Compensation (state) {
			return state.co2Compensation
		},
		cartCo2CompensationAmount (state) {
			return state.serverData
				? state.serverData.channel.location.co2CompensationAmount
				: 0
		},
		cartChangedItems(state) {
			// TODO: currently its possible that the cartitem was removed and so the reference is broken
			return state.changedItems
				?	state.changedItems
						.map(v => ({
							...v,
							item: state.items.find(item => item.id == v.id)
						}))
				: []
		},
		cartOrderNow(state) {
			return state.serverData
				? state.serverData.orderNow
				: false
		},
		cartOrderNowAvailable(state) {
			return state.serverData
				? state.serverData.orderNowAvailable
				: false
		},
		cartUpdateAt(state) {
			return state.updatedAt
		},
		cartLoading(state) {
			return state.loading
		},
		cartBigOrderText(state) {
			return state.serverData
				? state.serverData.bigOrderText
				: null;
		},
		showContinue(state) {
			return !!state.showContinue;
		},
		cartIsDetached(state) {
			return !!state.isDetached;
		},
		cartChannelHasDishesEnabled (state) {
			return state.serverData
				? state.serverData.channel.dishesEnabled
				: false
		},
		cartHasItemsWithDishEnabled (state, getters) {
			return state.items.some(v => v.dishEnabled)
		},
		cartZip(state) {
			return state.serverData?.zip
		},
		cartIsGroupOrderLocked(state) {
			return !!state.serverData?.isGroupOrderLocked
		},
    cartHasAddress(state, getters) {
      return !!state.serverData?.shippingAddress || getters.orderStates?.shippingAddressState === 'set'
    }
	},
	mutations: {
		...template.mutations,
		setData: generateSetChild("serverData"),
		addItem: generateAddArrayChild("items"),
		updateItem: generateUpdateArrayChild("items"),
		removeItem: generateRemoveArrayChild("items"),
		setBasketToken: generateSetChild("basketToken"),
		setBasketChannel: generateSetChild("basketChannel"),
		setLoading: generateSetChild("loading"),
		setOpenRequests: generateSetChild("openRequests"),
		setChangedItems: generateSetChild("changedItems"),
		setUpdatedAt: generateSetChild("updatedAt"),
		setTip: generateSetChild('tip'),
		setCo2Compensation: generateSetChild('co2Compensation'),
		setProduct: generateSetMapChild('productMap', 'code'),
		setProducts (state, { items }) {
			items.forEach(v => state.productMap[v.code] = v)
		},
		setShowContinue: generateSetChild('showContinue'),
		setIsDetached: generateSetChild('isDetached'),
	}
}
